/**
 * sl-qi4j.6: Shared MatchConfig Editor Component
 *
 * Reusable match configuration UI for both row matching (OutputTarget)
 * and relation lookup (FieldMapping) contexts.
 *
 * SOLID:
 * - S: Single Responsibility — only renders match config dropdowns
 * - I: Interface Segregation — optional props control which sections appear
 * - D: Dependency Inversion — pure presentational, no service dependencies
 *
 * DRY: One component for both row matching and relation lookup contexts.
 */
import { Menu, MenuItem } from '@affine/component';
import { InformationIcon } from '@blocksuite/icons/rc';
import { cssVarV2 } from '@streamlain/theme/v2';
import { type FC, forwardRef } from 'react';

import type {
  MultiMatchBehavior,
  StealBehavior,
  TargetMatchMode,
} from '../../services/automation';
import { TypeChip } from './type-chip';

// =============================================================================
// Option Constants (shared across all contexts)
// =============================================================================

/**
 * Match mode options with 'auto' — for row matching.
 */
export const MATCH_MODE_WITH_AUTO_OPTIONS: Array<{
  value: TargetMatchMode;
  label: string;
  description: string;
}> = [
  {
    value: 'auto',
    label: 'Auto (Recommended)',
    description: 'Try exact, starts with, then contains',
  },
  {
    value: 'exact',
    label: 'Exact Match',
    description: 'Value must match exactly',
  },
  {
    value: 'contains',
    label: 'Contains',
    description: 'Row contains the value',
  },
  {
    value: 'starts_with',
    label: 'Starts With',
    description: 'Row value starts with search term',
  },
];

/**
 * Match mode options without 'auto' — for relation lookup.
 */
export const MATCH_MODE_OPTIONS: Array<{
  value: TargetMatchMode;
  label: string;
  description: string;
}> = MATCH_MODE_WITH_AUTO_OPTIONS.filter(o => o.value !== 'auto');

/**
 * Not-found behavior options.
 */
export const NOT_FOUND_BEHAVIOR_OPTIONS: Array<{
  value: 'fail' | 'create';
  label: string;
  description: string;
}> = [
  {
    value: 'create',
    label: 'Create new row',
    description: 'Insert a new row if no match found (upsert)',
  },
  {
    value: 'fail',
    label: 'Fail',
    description: 'Report error if no matching row found',
  },
];

/**
 * Multi-match behavior options.
 */
export const MULTI_MATCH_BEHAVIOR_OPTIONS: Array<{
  value: MultiMatchBehavior;
  label: string;
  description: string;
}> = [
  {
    value: 'fail',
    label: 'Fail',
    description: 'Report error if multiple rows match',
  },
  {
    value: 'update_all',
    label: 'Update all',
    description: 'Update every matching row',
  },
  {
    value: 'update_first',
    label: 'Update first',
    description: 'Update only the first match found',
  },
  {
    value: 'skip',
    label: 'Skip',
    description: 'Skip this row, log a warning',
  },
  {
    value: 'ask',
    label: 'Ask me',
    description: 'Create a pending decision for manual resolution',
  },
];

/**
 * sl-tah4.4: Steal behavior options (for relations with inverseCardinality='one').
 */
export const STEAL_BEHAVIOR_OPTIONS: Array<{
  value: StealBehavior;
  label: string;
  description: string;
}> = [
  {
    value: 'fail',
    label: 'Fail',
    description: 'Report error if target row is already linked',
  },
  {
    value: 'silent_steal',
    label: 'Overwrite silently',
    description: 'Remove old link and set new one without error',
  },
  {
    value: 'ask',
    label: 'Ask me',
    description: 'Create a pending decision for manual resolution',
  },
];

// =============================================================================
// Column info for column selector
// =============================================================================

interface ColumnOption {
  id: string;
  name: string;
  type: string;
}

// =============================================================================
// Shared Dropdown Button Component (DRY)
// =============================================================================

interface DropdownButtonProps {
  label: string;
}

// forwardRef required: Radix DropdownMenu.Trigger asChild merges ref into child
const DropdownButton = forwardRef<HTMLButtonElement, DropdownButtonProps>(
  ({ label, ...props }, ref) => (
    <button
      ref={ref}
      {...props}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '3px 8px',
        fontSize: '11px',
        borderRadius: '4px',
        border: '1px solid var(--streamlain-border-color)',
        backgroundColor: 'var(--streamlain-background-primary-color)',
        color: 'var(--streamlain-text-primary-color)',
        cursor: 'pointer',
      }}
    >
      {label}
      <span style={{ color: cssVarV2('text/tertiary'), fontSize: '8px' }}>
        ▼
      </span>
    </button>
  )
);
DropdownButton.displayName = 'DropdownButton';

// =============================================================================
// Shared Menu Item Component (DRY)
// =============================================================================

interface OptionMenuItemProps {
  label: string;
  description: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}

const OptionMenuItem: FC<OptionMenuItemProps> = ({
  label,
  description,
  selected,
  disabled,
  onClick,
}) => (
  <MenuItem
    key={label}
    onClick={() => {
      if (!disabled) onClick();
    }}
    selected={selected}
  >
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      title={disabled ? description : undefined}
    >
      <span>{label}</span>
      <span style={{ fontSize: '10px', opacity: 0.6 }}>{description}</span>
    </div>
  </MenuItem>
);

// =============================================================================
// MatchConfigEditor Props
// =============================================================================

export interface MatchConfigEditorProps {
  /** Info text shown at top of the config section */
  infoText?: string;

  // --- Match Mode (always shown) ---
  matchMode: string;
  matchModeOptions: Array<{
    value: string;
    label: string;
    description: string;
  }>;
  onMatchModeChange: (mode: string) => void;

  // --- Not-Found Behavior (optional, typically for row matching) ---
  notFoundBehavior?: 'fail' | 'create';
  onNotFoundBehaviorChange?: (behavior: 'fail' | 'create') => void;
  /** Disable 'create' option (e.g. when source === target table) */
  disableCreate?: boolean;
  disableCreateReason?: string;

  // --- Multi-Match Behavior (optional) ---
  multiMatchBehavior?: MultiMatchBehavior;
  onMultiMatchBehaviorChange?: (behavior: MultiMatchBehavior) => void;

  // --- Column Selector (optional, typically for relation lookup) ---
  columns?: ColumnOption[];
  selectedColumnId?: string | undefined;
  onColumnChange?: (columnId: string | undefined) => void;

  // --- sl-tah4.4: Steal Behavior (for relation with inverseCardinality='one') ---
  stealBehavior?: StealBehavior;
  onStealBehaviorChange?: (behavior: StealBehavior) => void;
}

// =============================================================================
// MatchConfigEditor Component
// =============================================================================

export const MatchConfigEditor: FC<MatchConfigEditorProps> = ({
  infoText,
  matchMode,
  matchModeOptions,
  onMatchModeChange,
  notFoundBehavior,
  onNotFoundBehaviorChange,
  disableCreate,
  disableCreateReason,
  multiMatchBehavior,
  onMultiMatchBehaviorChange,
  columns,
  selectedColumnId,
  onColumnChange,
  stealBehavior,
  onStealBehaviorChange,
}) => {
  const showNotFound = onNotFoundBehaviorChange !== undefined;
  const showMultiMatch = onMultiMatchBehaviorChange !== undefined;
  const showColumnSelector =
    onColumnChange !== undefined && columns && columns.length > 0;
  const showStealBehavior = onStealBehaviorChange !== undefined;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      {/* Info text */}
      {infoText && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <InformationIcon
            style={{ width: '14px', height: '14px', opacity: 0.5 }}
          />
          <span style={{ fontSize: '11px', opacity: 0.7 }}>{infoText}</span>
        </div>
      )}

      {/* Row 1: Match Mode + optional Not-Found (50/50 split) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Match Mode */}
        <div
          style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <span style={{ fontSize: '11px', opacity: 0.6 }}>Match:</span>
          <Menu
            items={
              <>
                {matchModeOptions.map(opt => (
                  <OptionMenuItem
                    key={opt.value}
                    label={opt.label}
                    description={opt.description}
                    selected={matchMode === opt.value}
                    onClick={() => onMatchModeChange(opt.value)}
                  />
                ))}
              </>
            }
          >
            <DropdownButton
              label={
                matchModeOptions.find(o => o.value === matchMode)?.label ??
                matchModeOptions[0]?.label ??
                'Select'
              }
            />
          </Menu>
        </div>

        {/* Not-Found Behavior (optional, 50%) */}
        {showNotFound && (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span style={{ fontSize: '11px', opacity: 0.6 }}>
              If not found:
            </span>
            <Menu
              items={
                <>
                  {NOT_FOUND_BEHAVIOR_OPTIONS.map(opt => (
                    <OptionMenuItem
                      key={opt.value}
                      label={opt.label}
                      description={
                        disableCreate && opt.value === 'create'
                          ? (disableCreateReason ?? opt.description)
                          : opt.description
                      }
                      selected={(notFoundBehavior ?? 'create') === opt.value}
                      disabled={disableCreate && opt.value === 'create'}
                      onClick={() => {
                        if (disableCreate && opt.value === 'create') return;
                        onNotFoundBehaviorChange(opt.value);
                      }}
                    />
                  ))}
                </>
              }
            >
              <DropdownButton
                label={
                  NOT_FOUND_BEHAVIOR_OPTIONS.find(
                    o => o.value === (notFoundBehavior ?? 'create')
                  )?.label ?? 'Create new row'
                }
              />
            </Menu>
          </div>
        )}

        {/* Column Selector (optional, for relation lookup, 50%) */}
        {showColumnSelector && !showNotFound && (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span style={{ fontSize: '11px', opacity: 0.6 }}>In column:</span>
            <Menu
              items={
                <>
                  {columns
                    .filter(c =>
                      [
                        'title',
                        'rich-text',
                        'text',
                        'number',
                        'select',
                      ].includes(c.type)
                    )
                    .map(col => (
                      <MenuItem
                        key={col.id}
                        onClick={() =>
                          onColumnChange(
                            col.type === 'title' ? undefined : col.id
                          )
                        }
                        selected={
                          col.type === 'title'
                            ? !selectedColumnId
                            : selectedColumnId === col.id
                        }
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          <span>{col.name}</span>
                          <TypeChip type={col.type} small />
                        </div>
                      </MenuItem>
                    ))}
                </>
              }
            >
              <DropdownButton
                label={
                  selectedColumnId
                    ? (columns.find(c => c.id === selectedColumnId)?.name ??
                      'Title')
                    : 'Title (default)'
                }
              />
            </Menu>
          </div>
        )}
      </div>

      {/* Row 2: Column selector when both not-found AND column are present */}
      {showColumnSelector && showNotFound && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '11px', opacity: 0.6 }}>In column:</span>
          <Menu
            items={
              <>
                {columns
                  .filter(c =>
                    ['title', 'rich-text', 'text', 'number', 'select'].includes(
                      c.type
                    )
                  )
                  .map(col => (
                    <MenuItem
                      key={col.id}
                      onClick={() =>
                        onColumnChange(
                          col.type === 'title' ? undefined : col.id
                        )
                      }
                      selected={
                        col.type === 'title'
                          ? !selectedColumnId
                          : selectedColumnId === col.id
                      }
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <span>{col.name}</span>
                        <TypeChip type={col.type} small />
                      </div>
                    </MenuItem>
                  ))}
              </>
            }
          >
            <DropdownButton
              label={
                selectedColumnId
                  ? (columns.find(c => c.id === selectedColumnId)?.name ??
                    'Title')
                  : 'Title (default)'
              }
            />
          </Menu>
        </div>
      )}

      {/* Row 3: Multi-Match Behavior (full width) */}
      {showMultiMatch && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '11px', opacity: 0.6 }}>
            If multiple found:
          </span>
          <Menu
            items={
              <>
                {MULTI_MATCH_BEHAVIOR_OPTIONS.map(opt => (
                  <OptionMenuItem
                    key={opt.value}
                    label={opt.label}
                    description={opt.description}
                    selected={(multiMatchBehavior ?? 'fail') === opt.value}
                    onClick={() => onMultiMatchBehaviorChange(opt.value)}
                  />
                ))}
              </>
            }
          >
            <DropdownButton
              label={
                MULTI_MATCH_BEHAVIOR_OPTIONS.find(
                  o => o.value === (multiMatchBehavior ?? 'fail')
                )?.label ?? 'Fail'
              }
            />
          </Menu>
        </div>
      )}

      {/* sl-tah4.4: Steal Behavior — only for relations with inverseCardinality='one' */}
      {showStealBehavior && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '11px', opacity: 0.6 }}>
            If already linked:
          </span>
          <Menu
            items={
              <>
                {STEAL_BEHAVIOR_OPTIONS.map(opt => (
                  <OptionMenuItem
                    key={opt.value}
                    label={opt.label}
                    description={opt.description}
                    selected={(stealBehavior ?? 'fail') === opt.value}
                    onClick={() => onStealBehaviorChange(opt.value)}
                  />
                ))}
              </>
            }
          >
            <DropdownButton
              label={
                STEAL_BEHAVIOR_OPTIONS.find(
                  o => o.value === (stealBehavior ?? 'fail')
                )?.label ?? 'Fail'
              }
            />
          </Menu>
        </div>
      )}
    </div>
  );
};
